import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Trophy, RotateCcw, Share2, HelpCircle, X } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useGameLeaderboard } from '../hooks/useGameLeaderboard';
import { useNostrProfile } from '../hooks/useNostrQuery';

// ── Word List ───────────────────────────────────────────────────
// BCH / crypto / finance themed 5-letter words + common English words
// to keep it fun and accessible. ~300 words ensures variety.
const WORD_LIST = [
  // Crypto / BCH themed
  'BLOCK','CHAIN','COINS','MONEY','TRADE','VALUE','STAKE','MINER','NODES',
  'CRYPT','VAULT','TOKEN','ASSET','BANKS','FUNDS','GAINS','YIELD','STOCK',
  'PRICE','PENNY','POUND','BILLS','DEBIT','SPEND','STORE','SAVED','SENDS',
  'SCALE','SPEED','SPLIT','SMART','PROOF','TRUST','LEDGE','MERGE',
  'FLASH','QUICK','WORLD','BASED','POWER','POINT','PRIME','EARLY',
  // Common 5-letter words for variety
  'ABOUT','ABOVE','AFTER','AGAIN','AGREE','ALONG','ALPHA','ANGEL','APPLE',
  'ARENA','BADGE','BASIC','BEACH','BEGAN','BEGIN','BEING','BELOW','BIRTH',
  'BLAST','BLAZE','BLEND','BLOOM','BOARD','BONUS','BOOST','BOUND','BRAIN',
  'BRAVE','BREAD','BREAK','BRICK','BRIEF','BRING','BROAD','BROOK','BROWN',
  'BUILD','BURST','CANDY','CATCH','CAUSE','CHARM','CHASE','CHEAP','CHECK',
  'CHIEF','CHILD','CLAIM','CLASH','CLEAN','CLEAR','CLIMB','CLOCK','CLOSE',
  'CLOUD','COACH','COAST','COLOR','COUNT','COVER','CRAFT','CRANE','CRASH',
  'CRAZY','CREAM','CRISP','CROSS','CROWD','CRUSH','CURVE','CYCLE','DAILY',
  'DANCE','DEALS','DELTA','DEPTH','DIGIT','DOUBT','DRAFT','DRAIN','DRAWN',
  'DREAM','DRESS','DRIFT','DRINK','DRIVE','EARTH','EMPTY','ENEMY','ENJOY',
  'ENTER','EQUAL','ERROR','EVENT','EVERY','EXACT','EXIST','EXTRA','FAINT',
  'FAITH','FALSE','FANCY','FEAST','FIBER','FIELD','FIGHT','FINAL','FLAME',
  'FLOAT','FLOOR','FOCUS','FORCE','FORGE','FOUND','FRAME','FRESH','FRONT',
  'FRUIT','GAUGE','GIANT','GIVEN','GLASS','GLEAM','GLOBE','GRACE','GRAIN',
  'GRAND','GRANT','GRAPH','GRASP','GRASS','GRAVE','GREAT','GREEN','GRIND',
  'GROUP','GROVE','GROWN','GUARD','GUESS','GUIDE','HABIT','HAPPY','HARSH',
  'HAVEN','HEART','HEAVY','HONOR','HORSE','HOTEL','HOUSE','HUMAN','HUMOR',
  'IDEAL','IMAGE','INDEX','INNER','INPUT','INTRO','IVORY','JEWEL','JOINT',
  'JUDGE','JUICE','KARMA','KNACK','LABEL','LARGE','LASER','LATER','LAUGH',
  'LAYER','LEARN','LEAST','LEVEL','LEVER','LIGHT','LIMIT','LINEN','LIVER',
  'LOCAL','LOGIC','LOOSE','LUCKY','LUNAR','LUNCH','MAGIC','MAJOR','MAKER',
  'MANOR','MAPLE','MARCH','MATCH','MAYOR','MEDIA','METAL','MIGHT','MINOR',
  'MODEL','MONTH','MORAL','MOTOR','MOUNT','MOUTH','MOVED','MUSIC','NAVAL',
  'NERVE','NEWLY','NIGHT','NOBLE','NOISE','NORTH','NOTED','NOVEL','NURSE',
  'OCEAN','OFFER','OFTEN','ORBIT','ORDER','OTHER','OUTER','OWNER','OXIDE',
  'PANEL','PAPER','PARTY','PATCH','PAUSE','PEACE','PEARL','PHASE','PHONE',
  'PIANO','PIECE','PILOT','PIXEL','PLACE','PLAIN','PLANE','PLANT','PLATE',
  'PLAZA','PLUMB','PLUME','POKER','POLAR','PROUD','PUNCH','PUPIL','QUEEN',
  'QUEST','QUIET','QUOTE','RADAR','RADIO','RAISE','RANGE','RAPID','RATIO',
  'REACH','REALM','REBEL','REIGN','RELAX','REPLY','RIDER','RIDGE','RIGHT',
  'RIGID','RIVAL','RIVER','ROBIN','ROBOT','ROCKY','ROYAL','RURAL','SAINT',
  'SCENE','SCOPE','SCOUT','SENSE','SERVE','SETUP','SEVEN','SHADE','SHAKE',
  'SHALL','SHAPE','SHARE','SHARP','SHELL','SHIFT','SHINE','SHIRT','SHOCK',
  'SHORT','SHOUT','SIGHT','SILLY','SINCE','SIXTH','SIXTY','SIZED','SKILL',
  'SLEEP','SLICE','SLIDE','SLOPE','SMILE','SMOKE','SNACK','SOLAR','SOLID',
  'SOLVE','SORRY','SOUTH','SPACE','SPARE','SPARK','SPEAK','SPICE','SPINE',
  'SPORT','SQUAD','STAGE','STAND','START','STATE','STEAM','STEEL','STEEP',
  'STERN','STILL','STORM','STORY','STOVE','STRIP','STUFF','STYLE','SUGAR',
  'SUITE','SUNNY','SUPER','SURGE','SWEET','SWEPT','SWIFT','SWING','TABLE',
  'TASTE','THICK','THING','THINK','THIRD','THREW','THROW','TIGHT','TIMER',
  'TITLE','TOAST','TODAY','TOTAL','TOUCH','TOUGH','TOWER','TRACK','TRAIL',
  'TRAIN','TREAT','TREND','TRIAL','TRIBE','TRICK','TRULY','TUMOR','TWIST',
  'ULTRA','UNDER','UNION','UNITE','UNITY','UNTIL','UPPER','UPSET','URBAN',
  'USAGE','USUAL','UTTER','VALID','VERSE','VIDEO','VINYL','VIRAL','VISIT',
  'VITAL','VIVID','VOCAL','VOICE','WATCH','WATER','WEAVE','WHALE','WHEAT',
  'WHEEL','WHERE','WHICH','WHILE','WHITE','WHOLE','WHOSE','WIDTH','WOMAN',
  'WORTH','WOUND','WROTE','YOUTH','ZONES',
];

const WORD_LENGTH = 5;
const MAX_GUESSES = 6;

// Filter out any words that aren't exactly 5 letters (safety net)
const SAFE_WORD_LIST = WORD_LIST.filter(w => w.length === WORD_LENGTH);

// Valid guesses = all the target words (case insensitive check)
const VALID_WORDS = new Set(SAFE_WORD_LIST.map(w => w.toUpperCase()));

// ── Daily word selection ────────────────────────────────────────
// Uses the date as a seed so everyone gets the same daily word
function getDailyWord(): string {
  const now = new Date();
  const dayIndex = Math.floor(now.getTime() / 86400000); // days since epoch
  return SAFE_WORD_LIST[dayIndex % SAFE_WORD_LIST.length];
}

function getDayKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

// ── Tile states ─────────────────────────────────────────────────
type TileState = 'correct' | 'present' | 'absent' | 'empty' | 'tbd';

function evaluateGuess(guess: string, answer: string): TileState[] {
  const result: TileState[] = Array(WORD_LENGTH).fill('absent');
  const answerChars = answer.split('');
  const guessChars = guess.split('');
  const used = Array(WORD_LENGTH).fill(false);

  // First pass: mark correct positions
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guessChars[i] === answerChars[i]) {
      result[i] = 'correct';
      used[i] = true;
    }
  }

  // Second pass: mark present (right letter, wrong position)
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (result[i] === 'correct') continue;
    for (let j = 0; j < WORD_LENGTH; j++) {
      if (!used[j] && guessChars[i] === answerChars[j]) {
        result[i] = 'present';
        used[j] = true;
        break;
      }
    }
  }

  return result;
}

// ── Persistence ─────────────────────────────────────────────────
interface SavedState {
  dayKey: string;
  guesses: string[];
  gameOver: boolean;
  won: boolean;
}

function loadState(): SavedState | null {
  try {
    const raw = localStorage.getItem('bchnostr:wordle');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.dayKey !== getDayKey()) return null; // new day
    // If saved game has a stale/invalid state (e.g. word list changed), reset
    if (parsed.guesses?.length > MAX_GUESSES) return null;
    return parsed;
  } catch { return null; }
}

function saveState(state: SavedState) {
  try { localStorage.setItem('bchnostr:wordle', JSON.stringify(state)); } catch {}
}

function loadStats() {
  try {
    const raw = localStorage.getItem('bchnostr:wordle-stats');
    if (!raw) return { played: 0, won: 0, streak: 0, maxStreak: 0, distribution: [0,0,0,0,0,0] };
    return JSON.parse(raw);
  } catch { return { played: 0, won: 0, streak: 0, maxStreak: 0, distribution: [0,0,0,0,0,0] }; }
}

function saveStats(stats: any) {
  try { localStorage.setItem('bchnostr:wordle-stats', JSON.stringify(stats)); } catch {}
}

// ── Keyboard layout ─────────────────────────────────────────────
const KB_ROWS = [
  ['Q','W','E','R','T','Y','U','I','O','P'],
  ['A','S','D','F','G','H','J','K','L'],
  ['ENTER','Z','X','C','V','B','N','M','BACK'],
];

// ── Component ───────────────────────────────────────────────────
export default function WordlePage() {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const answer = getDailyWord();
  const { scores: leaderboard, loading: lbLoading, submitScore: submitLeaderboardScore, myScore: myLeaderboardScore } = useGameLeaderboard('wordle', user?.pubkey);

  const saved = loadState();
  const [guesses, setGuesses] = useState<string[]>(saved?.guesses || []);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameOver, setGameOver] = useState(saved?.gameOver || false);
  const [won, setWon] = useState(saved?.won || false);
  const [shake, setShake] = useState(false);
  const [revealRow, setRevealRow] = useState(-1);
  const [showHelp, setShowHelp] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [message, setMessage] = useState('');
  const messageTimer = useRef<any>(null);

  // Build keyboard state from all guesses
  const keyStates = useCallback(() => {
    const states: Record<string, TileState> = {};
    for (const guess of guesses) {
      const eval_ = evaluateGuess(guess, answer);
      for (let i = 0; i < WORD_LENGTH; i++) {
        const letter = guess[i];
        const state = eval_[i];
        const current = states[letter];
        // Priority: correct > present > absent
        if (!current || state === 'correct' || (state === 'present' && current === 'absent')) {
          states[letter] = state;
        }
      }
    }
    return states;
  }, [guesses, answer]);

  const showMessage = (msg: string, duration = 1500) => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
    setMessage(msg);
    messageTimer.current = setTimeout(() => setMessage(''), duration);
  };

  const submitGuess = useCallback(() => {
    if (gameOver) return;
    if (currentGuess.length !== WORD_LENGTH) {
      setShake(true);
      showMessage('Not enough letters');
      setTimeout(() => setShake(false), 500);
      return;
    }

    if (!VALID_WORDS.has(currentGuess.toUpperCase())) {
      setShake(true);
      showMessage('Not in word list');
      setTimeout(() => setShake(false), 500);
      return;
    }

    const newGuesses = [...guesses, currentGuess.toUpperCase()];
    const isWin = currentGuess.toUpperCase() === answer;
    const isLoss = newGuesses.length >= MAX_GUESSES && !isWin;

    setRevealRow(newGuesses.length - 1);
    setTimeout(() => setRevealRow(-1), 1800);

    setGuesses(newGuesses);
    setCurrentGuess('');

    if (isWin) {
      setTimeout(() => {
        setGameOver(true);
        setWon(true);
        const stats = loadStats();
        stats.played++;
        stats.won++;
        stats.streak++;
        stats.maxStreak = Math.max(stats.maxStreak, stats.streak);
        stats.distribution[newGuesses.length - 1]++;
        saveStats(stats);
        showMessage(['Genius!','Magnificent!','Impressive!','Splendid!','Great!','Phew!'][newGuesses.length - 1], 3000);
      }, 1800);
    } else if (isLoss) {
      setTimeout(() => {
        setGameOver(true);
        const stats = loadStats();
        stats.played++;
        stats.streak = 0;
        saveStats(stats);
        showMessage(answer, 5000);
      }, 1800);
    }

    saveState({ dayKey: getDayKey(), guesses: newGuesses, gameOver: isWin || isLoss, won: isWin });
  }, [currentGuess, guesses, gameOver, answer]);

  const handleKey = useCallback((key: string) => {
    if (gameOver) return;
    if (key === 'ENTER') {
      submitGuess();
    } else if (key === 'BACK' || key === 'BACKSPACE') {
      setCurrentGuess(prev => prev.slice(0, -1));
    } else if (/^[A-Z]$/i.test(key) && currentGuess.length < WORD_LENGTH) {
      setCurrentGuess(prev => (prev + key.toUpperCase()).slice(0, WORD_LENGTH));
    }
  }, [currentGuess, gameOver, submitGuess]);

  // Physical keyboard
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (showHelp || showStats) return;
      const key = e.key.toUpperCase();
      if (key === 'ENTER' || key === 'BACKSPACE' || /^[A-Z]$/.test(key)) {
        e.preventDefault();
        handleKey(key);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleKey, showHelp, showStats]);

  const shareResult = async () => {
    const grid = guesses.map(g => {
      const eval_ = evaluateGuess(g, answer);
      return eval_.map(s => s === 'correct' ? '\uD83D\uDFE9' : s === 'present' ? '\uD83D\uDFE8' : '\u2B1B').join('');
    }).join('\n');

    const text = `BCH Wordle ${getDayKey()} ${won ? guesses.length : 'X'}/${MAX_GUESSES}\n\n${grid}\n\nPlay at bchnostr.com/games/wordle`;

    if (user) {
      try {
        await publish({
          kind: 1,
          content: text,
          tags: [['t', 'bch'], ['t', 'bitcoincash'], ['t', 'bchnostr-wordle']],
          created_at: Math.floor(Date.now() / 1000),
        });
        showMessage('Shared to Nostr!', 2000);
      } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(text);
        showMessage('Copied to clipboard!', 2000);
      } catch {}
    }
  };

  // Auto-submit stats to leaderboard when game ends
  useEffect(() => {
    if (gameOver && user) {
      const stats = loadStats();
      // Score = maxStreak * 100 + winRate (so streaks matter most, win% is tiebreaker)
      const winRate = stats.played > 0 ? Math.round((stats.won / stats.played) * 100) : 0;
      const compositeScore = stats.maxStreak * 100 + winRate;
      if (compositeScore > 0) {
        submitLeaderboardScore(compositeScore, {
          meta: {
            maxStreak: stats.maxStreak,
            streak: stats.streak,
            played: stats.played,
            won: stats.won,
            winRate,
          },
        });
      }
    }
  }, [gameOver]);

  const states = keyStates();

  return (
    <div>
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/games" className="p-1 rounded-lg hover:bg-muted -ml-1">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-lg font-bold">BCH Wordle</h1>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setShowHelp(true)} className="p-2 rounded-lg hover:bg-muted text-muted-foreground">
              <HelpCircle className="w-5 h-5" />
            </button>
            <button onClick={() => setShowStats(true)} className="p-2 rounded-lg hover:bg-muted text-muted-foreground">
              <Trophy className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 flex flex-col items-center gap-4">
        {/* Message toast */}
        {message && (
          <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg bg-foreground text-background text-sm font-bold shadow-lg animate-bounce-in">
            {message}
          </div>
        )}

        {/* Board */}
        <div className="grid gap-1.5 my-2">
          {Array.from({ length: MAX_GUESSES }).map((_, row) => {
            const guess = row < guesses.length ? guesses[row] : (row === guesses.length ? currentGuess : '');
            const isSubmitted = row < guesses.length;
            const eval_ = isSubmitted ? evaluateGuess(guesses[row], answer) : [];
            const isShaking = shake && row === guesses.length;
            const isRevealing = revealRow === row;

            return (
              <div key={row} className={`flex gap-1.5 ${isShaking ? 'animate-shake' : ''}`}>
                {Array.from({ length: WORD_LENGTH }).map((_, col) => {
                  const letter = guess[col] || '';
                  const state: TileState = isSubmitted ? eval_[col] : (letter ? 'tbd' : 'empty');
                  const delay = isRevealing ? col * 300 : 0;

                  const bgClass = state === 'correct' ? 'bg-emerald-500 border-emerald-500 text-white'
                    : state === 'present' ? 'bg-amber-500 border-amber-500 text-white'
                    : state === 'absent' ? 'bg-muted-foreground/70 border-muted-foreground/70 text-white'
                    : state === 'tbd' ? 'border-muted-foreground/50 bg-card text-foreground scale-105'
                    : 'border-border bg-card';

                  return (
                    <div
                      key={col}
                      className={`w-[52px] h-[52px] sm:w-[58px] sm:h-[58px] flex items-center justify-center text-2xl font-extrabold uppercase border-2 rounded-lg transition-all duration-300 ${bgClass}`}
                      style={{
                        transitionDelay: isRevealing ? `${delay}ms` : '0ms',
                        animationDelay: isRevealing ? `${delay}ms` : '0ms',
                      }}
                    >
                      {letter}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Game over actions */}
        {gameOver && (
          <div className="flex flex-col items-center gap-3 py-2">
            <p className={`text-sm font-bold ${won ? 'text-emerald-500' : 'text-red-500'}`}>
              {won ? `Solved in ${guesses.length} guess${guesses.length === 1 ? '' : 'es'}!` : `The word was: ${answer}`}
            </p>
            <div className="flex gap-2">
              <button
                onClick={shareResult}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm"
              >
                <Share2 className="w-4 h-4" /> {user ? 'Share to Nostr' : 'Copy Result'}
              </button>
              <button
                onClick={() => setShowStats(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-muted text-foreground font-bold text-sm"
              >
                <Trophy className="w-4 h-4" /> Stats
              </button>
            </div>
          </div>
        )}

        {/* Keyboard */}
        <div className="w-full max-w-md space-y-1.5 mt-2">
          {KB_ROWS.map((row, ri) => (
            <div key={ri} className="flex justify-center gap-1">
              {row.map(key => {
                const isWide = key === 'ENTER' || key === 'BACK';
                const state = states[key];
                const bgClass = state === 'correct' ? 'bg-emerald-500 text-white border-emerald-500'
                  : state === 'present' ? 'bg-amber-500 text-white border-amber-500'
                  : state === 'absent' ? 'bg-muted-foreground/60 text-muted border-muted-foreground/60'
                  : 'bg-muted text-foreground border-border';

                return (
                  <button
                    key={key}
                    onClick={() => handleKey(key)}
                    className={`${isWide ? 'px-3 text-[10px]' : 'px-0 text-sm'} ${isWide ? 'min-w-[52px]' : 'min-w-[30px] sm:min-w-[36px]'} h-12 rounded-lg border font-bold uppercase flex items-center justify-center active:scale-95 transition-all ${bgClass}`}
                  >
                    {key === 'BACK' ? '\u232B' : key}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        {/* ── Leaderboard ── */}
        <div className="w-full max-w-md mt-4 rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-gradient-to-r from-emerald-500/10 to-transparent flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-emerald-500" />
              <h3 className="text-sm font-bold">Leaderboard</h3>
            </div>
            <span className="text-[10px] text-muted-foreground">
              {leaderboard.length > 0 ? `${leaderboard.length} player${leaderboard.length !== 1 ? 's' : ''}` : ''}
            </span>
          </div>

          {lbLoading && leaderboard.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Loading scores...</p>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-3xl mb-2">🏆</p>
              <p className="text-sm font-medium">No scores yet!</p>
              <p className="text-xs text-muted-foreground mt-1">
                {user ? 'Complete a game to appear on the board.' : 'Log in and play to be the first!'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {leaderboard.slice(0, 15).map((entry, i) => (
                <WordleLeaderboardRow key={entry.pubkey} entry={entry} rank={i + 1} isMe={entry.pubkey === user?.pubkey} />
              ))}
            </div>
          )}

          {/* My rank (if not in top 15) */}
          {myLeaderboardScore && leaderboard.findIndex(s => s.pubkey === user?.pubkey) >= 15 && (
            <div className="border-t-2 border-dashed border-emerald-500/30">
              <WordleLeaderboardRow
                entry={myLeaderboardScore}
                rank={leaderboard.findIndex(s => s.pubkey === user?.pubkey) + 1}
                isMe
              />
            </div>
          )}
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowHelp(false)}>
          <div className="bg-card rounded-2xl border border-border p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold">How to Play</h3>
              <button onClick={() => setShowHelp(false)} className="p-1 rounded-lg hover:bg-muted"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>Guess the <strong className="text-foreground">BCH Wordle</strong> in 6 tries.</p>
              <ul className="space-y-1.5 list-disc pl-4">
                <li>Each guess must be a valid 5-letter word.</li>
                <li>The color of the tiles will change to show how close your guess was.</li>
                <li>A new word is available each day!</li>
              </ul>
              <div className="space-y-2 pt-2 border-t border-border">
                <p className="text-xs font-semibold text-foreground">Examples</p>
                <div className="flex gap-1">
                  {['C','H','A','I','N'].map((l, i) => (
                    <div key={i} className={`w-9 h-9 flex items-center justify-center text-sm font-extrabold rounded border-2 ${
                      i === 0 ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-border'
                    }`}>{l}</div>
                  ))}
                </div>
                <p className="text-xs"><strong className="text-emerald-500">C</strong> is in the word and in the correct spot.</p>
                <div className="flex gap-1">
                  {['B','L','O','C','K'].map((l, i) => (
                    <div key={i} className={`w-9 h-9 flex items-center justify-center text-sm font-extrabold rounded border-2 ${
                      i === 2 ? 'bg-amber-500 border-amber-500 text-white' : 'border-border'
                    }`}>{l}</div>
                  ))}
                </div>
                <p className="text-xs"><strong className="text-amber-500">O</strong> is in the word but in the wrong spot.</p>
                <div className="flex gap-1">
                  {['M','I','N','E','R'].map((l, i) => (
                    <div key={i} className={`w-9 h-9 flex items-center justify-center text-sm font-extrabold rounded border-2 ${
                      i === 3 ? 'bg-zinc-600 border-zinc-600 text-white' : 'border-border'
                    }`}>{l}</div>
                  ))}
                </div>
                <p className="text-xs"><strong className="text-zinc-400">E</strong> is not in the word in any spot.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStats && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowStats(false)}>
          <div className="bg-card rounded-2xl border border-border p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold">Statistics</h3>
              <button onClick={() => setShowStats(false)} className="p-1 rounded-lg hover:bg-muted"><X className="w-5 h-5" /></button>
            </div>
            {(() => {
              const stats = loadStats();
              const maxDist = Math.max(1, ...stats.distribution);
              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <p className="text-2xl font-extrabold">{stats.played}</p>
                      <p className="text-[10px] text-muted-foreground">Played</p>
                    </div>
                    <div>
                      <p className="text-2xl font-extrabold">{stats.played > 0 ? Math.round(stats.won / stats.played * 100) : 0}</p>
                      <p className="text-[10px] text-muted-foreground">Win %</p>
                    </div>
                    <div>
                      <p className="text-2xl font-extrabold">{stats.streak}</p>
                      <p className="text-[10px] text-muted-foreground">Streak</p>
                    </div>
                    <div>
                      <p className="text-2xl font-extrabold">{stats.maxStreak}</p>
                      <p className="text-[10px] text-muted-foreground">Max</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Guess Distribution</p>
                    {stats.distribution.map((count: number, i: number) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs font-bold w-3 text-right">{i + 1}</span>
                        <div
                          className={`h-5 rounded-sm flex items-center justify-end px-1.5 text-[10px] font-bold text-white ${
                            won && guesses.length === i + 1 ? 'bg-emerald-500' : 'bg-zinc-600'
                          }`}
                          style={{ width: `${Math.max(8, (count / maxDist) * 100)}%` }}
                        >
                          {count}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake { animation: shake 0.5s ease-in-out; }
        @keyframes bounce-in {
          0% { transform: translateX(-50%) translateY(-10px); opacity: 0; }
          50% { transform: translateX(-50%) translateY(2px); }
          100% { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
        .animate-bounce-in { animation: bounce-in 0.3s ease-out; }
      `}</style>
    </div>
  );
}

// ── Wordle Leaderboard Row ───────────────────────────────────────
function WordleLeaderboardRow({ entry, rank, isMe }: { entry: import('../hooks/useGameLeaderboard').ScoreEntry; rank: number; isMe: boolean }) {
  const { profile } = useNostrProfile(entry.pubkey);
  const name = profile?.name || profile?.display_name || entry.pubkey.slice(0, 8) + '...';
  const avatar = profile?.picture;

  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;

  // Extract Wordle-specific stats from meta
  const maxStreak = entry.meta?.maxStreak ?? Math.floor(entry.score / 100);
  const winRate = entry.meta?.winRate ?? (entry.score % 100);
  const played = entry.meta?.played;

  return (
    <Link
      to={`/profile/${entry.pubkey}`}
      className={`flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors ${isMe ? 'bg-emerald-500/5' : ''}`}
    >
      {/* Rank */}
      <div className="w-7 text-center shrink-0">
        {medal ? (
          <span className="text-base">{medal}</span>
        ) : (
          <span className={`text-xs font-bold tabular-nums ${isMe ? 'text-emerald-500' : 'text-muted-foreground'}`}>{rank}</span>
        )}
      </div>

      {/* Avatar */}
      <div className="w-8 h-8 rounded-full bg-muted overflow-hidden shrink-0">
        {avatar ? (
          <img src={avatar} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs font-bold">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isMe ? 'text-emerald-500' : ''}`}>
          {name}
          {isMe && <span className="text-[10px] text-emerald-500/60 ml-1">(you)</span>}
        </p>
        {played != null && (
          <p className="text-[10px] text-muted-foreground">{played} games played</p>
        )}
      </div>

      {/* Stats */}
      <div className="text-right shrink-0 space-y-0.5">
        <p className={`text-sm font-bold tabular-nums ${rank <= 3 ? 'text-emerald-500' : isMe ? 'text-emerald-500' : ''}`}>
          {maxStreak} streak
        </p>
        <p className="text-[10px] text-muted-foreground tabular-nums">{winRate}% win</p>
      </div>
    </Link>
  );
}

